import { Check, X } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import type { MenuItem } from '../../../../components/Menu';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { DateCell } from '../../../../components/table/DateCell';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { formatDateOnly } from '../../../../lib/formatDate';
import { mySurveysApi } from '../mySurveysApi';
import { TRAINING_DECISION, myTrainingStatus, type MySurveyTrainingRecommendationRow } from '../mySurveyTypes';
import { MySurveyRecommendationDecisionModal } from './MySurveyRecommendationDecisionModal';

/**
 * Preview › Training Recommendations — the trainings SENT to the employee, in the STANDARD panel
 * table (product call, 13 Oct 2026; it replaced a stack of cards): search, sort and paging are the
 * server's, and the title opens that recommendation's own PROFILE, where it is answered and its
 * training recorded.
 *
 * **Accept and Decline are ON THE ROW** (product call, 13 Oct 2026) while a recommendation waits
 * for the employee's answer — `rowQuickActions`, two icon buttons named twice (label and tooltip),
 * on the key of the decision. Each opens the profile's own confirm form: the answer is FINAL, so it
 * is never one stray click. Once answered the buttons are gone; everything else is the profile's.
 *
 * NO checkboxes: nothing is done to several rows at once. No provenance columns either: the row is
 * the employee's track on a recommendation, not a record they created.
 */
export function MySurveyTrainingRecommendationsTable({
  surveyId,
  answered,
  readOnly = false,
}: {
  surveyId: string;
  answered: boolean;
  /** The survey is deleted: the tracks stay readable, and no answer is offered. */
  readOnly?: boolean;
}) {
  const navigate = useNavigate();
  // The row being answered, and which way; null while no form is open.
  const [deciding, setDeciding] = useState<{ row: MySurveyTrainingRecommendationRow; accept: boolean } | null>(null);

  const fetchPage = useCallback(
    (query: DataTableQuery, signal: AbortSignal) => mySurveysApi.trainingRecommendationsTable(surveyId, query, signal),
    [surveyId],
  );

  const state = useDataTable<MySurveyTrainingRecommendationRow>({ fetchPage, pageSize: 10, defaultSort: 'sentAt:desc' });

  const columns = useMemo<DataColumn<MySurveyTrainingRecommendationRow>[]>(
    () => [
      { key: 'title', header: 'Training', width: 300, sortable: true, render: (row) => <TextCell value={row.title} /> },
      {
        // Where they stand on it — their own lifecycle; a `stage` column, not the shell's live-or-deleted flag.
        key: 'trackStatus',
        header: 'Status',
        width: 190,
        stage: true,
        summary: true,
        render: (row) => <StatusText tone="inactive">{myTrainingStatus(row)}</StatusText>,
      },
      { key: 'sentAt', header: 'Sent', width: 190, sortable: true, summary: true, render: (row) => <DateCell value={row.sentAt} /> },
      {
        // Two calendar dates as one range — `formatDateOnly`, never the instant path.
        key: 'training',
        header: 'Training dates',
        width: 230,
        render: (row) => (
          <TextCell
            value={
              row.trainingStartDate != null && row.trainingEndDate != null
                ? `${formatDateOnly(row.trainingStartDate)} – ${formatDateOnly(row.trainingEndDate)}`
                : null
            }
            mono
          />
        ),
      },
      { key: 'completed', header: 'Ended', width: 190, render: (row) => <DateCell value={row.trainingCompletedAt ?? null} fallback="—" /> },
      { key: 'report', header: 'Report Submitted', width: 190, render: (row) => <DateCell value={row.reportAt ?? null} fallback="—" /> },
    ],
    [],
  );

  // Only a row still WAITING for an answer carries them — an answered one has nothing to decide.
  const rowQuickActions = useCallback(
    (row: MySurveyTrainingRecommendationRow): MenuItem[] =>
      row.decision !== TRAINING_DECISION.Pending
        ? []
        : [
            {
              key: 'accept',
              label: 'Accept',
              icon: <Check className="h-4 w-4" aria-hidden="true" />,
              permission: 'my-surveys.decide-plan',
              onClick: () => setDeciding({ row, accept: true }),
            },
            {
              key: 'decline',
              label: 'Decline',
              icon: <X className="h-4 w-4" aria-hidden="true" />,
              permission: 'my-surveys.decide-plan',
              onClick: () => setDeciding({ row, accept: false }),
            },
          ],
    [],
  );

  return (
    <>
      <DataTablePanel<MySurveyTrainingRecommendationRow>
        id="my-survey-training-recommendations"
        state={state}
        columns={columns}
        rowKey={(row) => row.id}
        searchPlaceholder="Search trainings"
        // Nothing is done to several rows at once.
        selectable={false}
        // Absent on a deleted survey — no gutter is drawn for buttons that could never appear.
        rowQuickActions={readOnly ? undefined : rowQuickActions}
        auditColumns={false}
        // The TITLE opens the recommendation's profile — the page's own read key.
        onRowOpen={(row) => navigate(`/self-service/surveys/${surveyId}/training-recommendations/${row.id}`)}
        openPermission="my-surveys.read"
        emptyMessage={
          answered
            ? 'No training recommendation has been sent to you yet. They appear here once they are.'
            : 'You have not answered this survey yet. Trainings recommended for you appear here once they are sent to you.'
        }
        emptySearchMessage="No training matches that search."
      />

      {deciding ? (
        <MySurveyRecommendationDecisionModal
          surveyId={surveyId}
          recommendationId={deciding.row.id}
          title={deciding.row.title}
          accept={deciding.accept}
          onClose={() => setDeciding(null)}
          onDecided={() => {
            setDeciding(null);
            state.reload();
          }}
        />
      ) : null}
    </>
  );
}
